import {Button} from '@/components/ui/button/Button'
import {DialogDisclosure, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {AlertCircleIcon, PencilLineIcon, PlusIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm, useWatch} from 'react-hook-form'
import {ChooseScaleFormSchema} from '../../formSchemas'
import {ChooseScaleDialog} from './ChooseScaleDialog'
import {EvaluationScaleExtended, ScaleType} from '../../types'
import {BarthelScaleDialog} from './BarthelScaleDialog'
import {ObjectEntries, raise} from '@/utilities/helpers'
import {useState} from 'react'
import dayjs from 'dayjs'
import {IadlScaleDialog} from './IadlScaleDialog'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTheme} from 'styled-components'
const removeChooseScaleAndTotalScoreFields = (evaluationScale: EvaluationScaleExtended) => {
    return Object.fromEntries(
        ObjectEntries(evaluationScale).filter(
            ([key]) => key != 'type' && key != 'evaluationDate' && key != 'totalScore'
        )
    )
}

type StoreEditEvaluationProps = {
    onSubmit: (evaluationScale: EvaluationScaleExtended) => void
    evaluationScale?: EvaluationScaleExtended
    temporaryEvaluations: EvaluationScaleExtended[]
    openingEventDate?: string
}

export const StoreEditEvaluation = ({
    onSubmit,
    evaluationScale,
    temporaryEvaluations,
    openingEventDate
}: StoreEditEvaluationProps) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const [open, setOpen] = useState(false)
    const [step, setStep] = useState<'chooseScale' | 'questions'>('chooseScale')

    const chooseScaleForm = useForm<ChooseScaleFormSchema>({
        resolver: zodResolver(ChooseScaleFormSchema),
        defaultValues: {
            evaluationDate: openingEventDate
                ? dayjs(openingEventDate).toDate()
                : evaluationScale?.evaluationDate
                  ? dayjs(evaluationScale.evaluationDate).toDate()
                  : undefined,
            type: evaluationScale?.type,
            totalScore: evaluationScale?.totalScore
        }
    })
    const evaluationScaleExtended = EvaluationScaleExtended.safeParse(evaluationScale)
    const typeWatch = useWatch({control: chooseScaleForm.control, name: 'type'})
    const evaluationDateWatch = useWatch({control: chooseScaleForm.control, name: 'evaluationDate'})

    const scaleToDialog = {
        [ScaleType.enum.barthel_index]: (
            <BarthelScaleDialog
                defaultValues={
                    evaluationScaleExtended.success
                        ? removeChooseScaleAndTotalScoreFields(evaluationScaleExtended.data)
                        : undefined
                }
                evaluationScaleCount={temporaryEvaluations.length + 1}
                onConfirm={(values, totalScore) =>
                    onSubmit({
                        ...values,
                        totalScore,
                        evaluationDate: dayjs(evaluationDateWatch).format('YYYY-MM-DD')
                    })
                }
            />
        ),
        [ScaleType.enum.lawton_iadl]: (
            <IadlScaleDialog
                defaultValues={
                    evaluationScaleExtended.success
                        ? removeChooseScaleAndTotalScoreFields(evaluationScaleExtended.data)
                        : undefined
                }
                evaluationScaleCount={temporaryEvaluations.length + 1}
                onConfirm={(values, totalScore) =>
                    onSubmit({
                        ...values,
                        totalScore,
                        evaluationDate: dayjs(evaluationDateWatch).format('YYYY-MM-DD')
                    })
                }
            />
        )
    } as const satisfies Record<Extract<ScaleType, 'barthel_index' | 'lawton_iadl'>, React.ReactNode>

    return (
        <DialogProvider
            setOpen={value => {
                chooseScaleForm.reset()
                setStep('chooseScale')
                setOpen(value)
            }}
            open={open}
        >
            <DialogDisclosure
                render={
                    evaluationScale ? (
                        <Button variant="ghost" shape="square" size="sm">
                            <PencilLineIcon />
                        </Button>
                    ) : temporaryEvaluations.length < 6 ? (
                        <Button variant="secondary" size="sm">
                            <PlusIcon />
                            {t('projects:add_evaluation_scale')}
                        </Button>
                    ) : (
                        <></>
                    )
                }
            />
            {!evaluationScale && temporaryEvaluations.length == 1 && (
                <Flexbox align="center" gap={1.5}>
                    <AlertCircleIcon size={16} fill={palette.warning[500]} />
                    <Text palette="warning" shade="800" size="textXs">
                        {t('projects:evaluation_scale_count_warning')}
                    </Text>
                </Flexbox>
            )}
            {step == 'questions' && (typeWatch == 'barthel_index' || typeWatch == 'lawton_iadl') ? (
                scaleToDialog[typeWatch]
            ) : (
                <ChooseScaleDialog
                    temporaryEvaluations={temporaryEvaluations}
                    form={chooseScaleForm}
                    onSubmit={() => {
                        if (typeWatch == 'barthel_index' || typeWatch == 'lawton_iadl') {
                            setStep('questions')
                        } else {
                            chooseScaleForm.handleSubmit(values => {
                                const baseScale = {
                                    evaluationDate: values.evaluationDate
                                        ? dayjs(values.evaluationDate).format('YYYY-MM-DD')
                                        : null,
                                    totalScore: values.totalScore ?? raise('totalScore is nullish'),
                                    type: values.type
                                }
                                onSubmit(baseScale as EvaluationScaleExtended)
                                setOpen(false)
                            })()
                        }
                    }}
                />
            )}
        </DialogProvider>
    )
}
